import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Suspense, useState } from "react";
import { useFetcher } from "@rest-hooks/core";
import { Grid, LinearProgress, Tooltip, Typography, makeStyles } from "@material-ui/core";
import ResetIcon from "@material-ui/icons/SettingsBackupRestore";
import DownloadIcon from "@material-ui/icons/CloudDownload";
import { DployTable } from "../DployTable";
import { SigningDocumentResource } from "@ploy-lib/rest-resources";
import { FormattedDate, defineMessages, useIntl } from "react-intl";
import { downloadBlob as downloadUtil } from "@ploy-lib/core";
import { useTenant } from "@ploy-ui/tenants";
const messages = defineMessages({
    title: {
        id: "core.application-signing.status.title",
        defaultMessage: "Status"
    },
    read: {
        id: "core.application-signing.status.read",
        defaultMessage: "Read"
    },
    documentGroup: {
        id: "core.application-signing.status.document-group",
        defaultMessage: "Document group"
    },
    partlySigned: {
        id: "core.application-signing.status.partially-signed",
        defaultMessage: "Partially signed"
    },
    signed: {
        id: "core.application-signing.status.signed",
        defaultMessage: "Signed"
    },
    reSign: {
        id: "core.application-signing.status.reset",
        defaultMessage: "Reset"
    },
    download: {
        id: "core.application-signing.status.download",
        defaultMessage: "Download"
    },
    error: {
        id: "core.application-signing.status.error",
        defaultMessage: "There was an error"
    },
    status: {
        id: "core.application-signing.status.status",
        defaultMessage: "Status"
    },
    personsToSign: {
        id: "core.application-signing.status.personsToSign",
        defaultMessage: "PersonsToSign"
    },
    downloadAllDocuments: {
        id: "core.application-signing.status.downloadAllDocuments",
        defaultMessage: "Download all documents"
    },
    notRead: {
        id: "core.application-signing.status.notRead",
        defaultMessage: "Not read"
    },
    notSigned: {
        id: "core.application-signing.status.notSigned",
        defaultMessage: "Not Signed"
    },
    downloadTooltip: {
        id: "core.application-signing.status.downloadTooltip",
        defaultMessage: "Last signed version of the document"
    }
});
const useStyles = makeStyles(theme => ({
    cellText: { fontSize: "12px", textWrap: "noWrap" }
}), { name: "AppSigningStatus" });
const AppSigningStatus = (props) => {
    const { documents, hideSigningActions, useIndividualSignerStatus } = props;
    const intl = useIntl();
    const classes = useStyles();
    const cellStyle = {
        fontSize: 12,
        width: "auto",
        whiteSpace: "nowrap"
    };
    const resetSignature = useFetcher(SigningDocumentResource.resetSignature());
    const [errorMessage, setErrorMessage] = useState("");
    const useColumnSort = useTenant().tenant?.settings?.useColmnsort ?? true;
    return !useIndividualSignerStatus ? (_jsxs(Suspense, { fallback: _jsx(LinearProgress, {}), children: [_jsx(DployTable, { title: intl.formatMessage(messages.title), columns: [
                    {
                        title: intl.formatMessage(messages.documentGroup),
                        field: "documentName",
                        sorting: useColumnSort,
                        cellStyle
                    },
                    {
                        title: intl.formatMessage(messages.read),
                        field: "read",
                        type: "boolean",
                        sorting: useColumnSort,
                        cellStyle
                    },
                    {
                        title: intl.formatMessage(messages.partlySigned),
                        field: "partiallySigned",
                        type: "boolean",
                        sorting: useColumnSort,
                        cellStyle
                    },
                    {
                        title: intl.formatMessage(messages.signed),
                        field: "signed",
                        type: "boolean",
                        sorting: useColumnSort,
                        cellStyle
                    }
                ], data: documents || [], actions: hideSigningActions
                    ? undefined
                    : [
                        ({ allowResetSignature, canResetSignature }) => allowResetSignature && canResetSignature
                            ? {
                                icon: () => _jsx(ResetIcon, {}),
                                tooltip: intl.formatMessage(messages.reSign),
                                onClick: (e, { esignTaskId }) => {
                                    if (!window.confirm("Dette vil og resette alle dokumenter hvor signatarer på dette dokumentet er berørt"))
                                        return;
                                    if (esignTaskId)
                                        return resetSignature({}, { id: esignTaskId });
                                }
                            }
                            : undefined,
                        ({ download, allowDocumentDownload, documentName, applicationNumber }) => allowDocumentDownload && download
                            ? {
                                icon: () => {
                                    return _jsx(DownloadIcon, {});
                                },
                                tooltip: intl.formatMessage(messages.download),
                                onClick: async (e) => {
                                    e.stopPropagation();
                                    setErrorMessage("");
                                    // const options = {
                                    // 	method: "get",
                                    // 	credentials: "same-origin",
                                    // 	headers: {
                                    // 		"Content-Type": "application/pdf"
                                    // 	}
                                    // };
                                    try {
                                        const response = await fetch(download);
                                        if (!response.ok) {
                                            throw new Error("Server error");
                                        }
                                        const blob = await response.blob();
                                        const fileName = `${documentName}_${applicationNumber}`;
                                        downloadUtil(blob, fileName);
                                    }
                                    catch (error) {
                                        console.error(error);
                                        setErrorMessage(intl.formatMessage(messages.error));
                                    }
                                }
                            }
                            : undefined
                    ], options: {
                    actionsColumnIndex: -1,
                    search: false,
                    draggable: false,
                    sorting: false,
                    headerStyle: cellStyle,
                    paging: documents != null && documents.length > 5,
                    showEmptyDataSourceMessage: false,
                    emptyRowsWhenPaging: false,
                    header: true,
                    idSynonym: "esignTaskId"
                } }), errorMessage && (_jsx(Typography, { color: "error", variant: "subtitle2", children: errorMessage }))] })) : (
    // New design that shows status for each induvidual signer
    _jsxs(Suspense, { fallback: _jsx(LinearProgress, {}), children: [_jsx(DployTable, { title: intl.formatMessage(messages.title), columns: [
                    {
                        title: intl.formatMessage(messages.documentGroup),
                        field: "documentName",
                        sorting: useColumnSort,
                        cellStyle
                    },
                    {
                        title: intl.formatMessage(messages.personsToSign),
                        field: "personsToSign",
                        type: "string",
                        sorting: useColumnSort,
                        cellStyle,
                        render: documents => (_jsx(Grid, { container: true, spacing: 1, children: documents.signersToSign.map((x) => {
                                if (!x.mustSign ||
                                    !(documents.signerTypes.some(y => y === x.signerType) ||
                                        documents.signerTypesToRead.some(y => y === x.signerType)))
                                    return null;
                                return (_jsx(Grid, { item: true, xs: 12, children: x.firstName + " " + x.lastName }));
                            }) }))
                    },
                    {
                        title: intl.formatMessage(messages.status),
                        field: "status",
                        type: "string",
                        sorting: useColumnSort,
                        cellStyle,
                        render: documents => (_jsx(Grid, { container: true, direction: "column", spacing: 1, style: {}, children: documents.signersToSign.map((x) => {
                                if (!x.mustSign ||
                                    !(documents.signerTypes.some(y => y === x.signerType) ||
                                        documents.signerTypesToRead.some(y => y === x.signerType)))
                                    return null;
                                if (documents.expectedStatusId === 2 &&
                                    documents.signers.includes(x.applicationSignerId)) {
                                    return (_jsx(Grid, { item: true, children: _jsxs(Typography, { className: classes.cellText, style: { color: "green" }, children: [intl.formatMessage(messages.signed) + " - ", _jsx(FormattedDate, { value: documents.signTimes?.find(y => x.applicationSignerId === y.key)?.value, format: "2-digit" })] }) }));
                                }
                                else if (documents.expectedStatusId === 3 &&
                                    documents.signers.includes(x.applicationSignerId)) {
                                    return (_jsx(Grid, { item: true, children: _jsxs(Typography, { className: classes.cellText, style: { color: "green" }, children: [intl.formatMessage(messages.read) + " - ", _jsx(FormattedDate, { value: documents.signTimes?.find(y => x.applicationSignerId === y.key)?.value, format: "2-digit" })] }) }));
                                }
                                else {
                                    return (_jsx(Grid, { item: true, children: _jsx(Typography, { className: classes.cellText, style: { color: "orange" }, children: documents.expectedStatusId === 3
                                                ? intl.formatMessage(messages.notRead)
                                                : intl.formatMessage(messages.notSigned) }) }));
                                }
                            }) }))
                    },
                    {
                        title: intl.formatMessage(messages.download),
                        field: "download",
                        type: "string",
                        sorting: useColumnSort,
                        cellStyle,
                        render: documents => (_jsx(Tooltip, { title: intl.formatMessage(messages.downloadTooltip), children: _jsx(Grid, { container: true, spacing: 1, onClick: async () => {
                                    setErrorMessage("");
                                    try {
                                        const response = await fetch(documents.download);
                                        if (!response.ok) {
                                            throw new Error("Server error");
                                        }
                                        const blob = await response.blob();
                                        const fileName = `${documents.documentCode}_${documents.applicationNumber}`;
                                        downloadUtil(blob, fileName);
                                    }
                                    catch (error) {
                                        console.error(error);
                                        setErrorMessage(intl.formatMessage(messages.error));
                                    }
                                }, style: { cursor: "pointer" }, children: documents.download != null ? (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, children: _jsx(Typography, { className: classes.cellText, style: { textDecoration: "underline" }, children: intl.formatMessage(messages.download) }) }), _jsx(Grid, { item: true, children: _jsx("i", { className: "fa fa-download" }) })] })) : null }) }))
                    }
                ], data: documents || [], options: {
                    actionsColumnIndex: -1,
                    search: false,
                    draggable: false,
                    sorting: false,
                    headerStyle: cellStyle,
                    paging: documents != null && documents.length > 5,
                    showEmptyDataSourceMessage: false,
                    emptyRowsWhenPaging: false,
                    header: true,
                    idSynonym: "esignTaskId",
                    overflowY: "visible"
                } }), _jsxs(Grid, { container: true, spacing: 1, style: { justifyContent: errorMessage ? "space-between" : "right" }, children: [_jsx(Grid, { item: true, children: errorMessage && (_jsx(Typography, { color: "error", variant: "subtitle2", children: errorMessage })) }), documents.filter(x => x.download !== undefined && x.download !== null)
                        .length > 0 ? (_jsx(Grid, { item: true, children: _jsx(Tooltip, { title: intl.formatMessage(messages.downloadTooltip), children: _jsxs(Grid, { container: true, spacing: 1, style: { cursor: "pointer" }, onClick: () => {
                                    setErrorMessage("");
                                    documents
                                        ?.filter(x => x.download != null)
                                        .forEach(async (doc) => {
                                        try {
                                            const response = await fetch(doc.download);
                                            if (!response.ok) {
                                                throw new Error("Server error");
                                            }
                                            const blob = await response.blob();
                                            const fileName = `${doc.documentCode}_${doc.applicationNumber}`;
                                            downloadUtil(blob, fileName);
                                        }
                                        catch (error) {
                                            console.error(error);
                                            setErrorMessage(intl.formatMessage(messages.error));
                                        }
                                    });
                                }, children: [_jsx(Grid, { item: true, children: _jsx(Typography, { className: classes.cellText, style: { textDecoration: "underline", fontSize: "14px" }, children: intl.formatMessage(messages.downloadAllDocuments) }) }), _jsx(Grid, { item: true, children: _jsx("i", { className: "fa fa-download" }) })] }) }) })) : (_jsx("div", {}))] })] }));
};
AppSigningStatus.displayName = "DployAppSigningStatus";
export { AppSigningStatus };
//# sourceMappingURL=AppSigningStatus.js.map